import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, UserRound } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const ro = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[var(--text-dim)]";

export default function Profile() {
  const { data, loading, reload } = useFetch("/user/profile");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [pwd, setPwd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { password: pwd };
      if (data.name_editable && form.name && form.name !== data.name) payload.name = form.name;
      if (data.email_editable && form.email && form.email !== data.email) payload.email = form.email;
      if (data.phone_editable && form.phone && form.phone !== (data.phone || "")) payload.phone = form.phone;
      if (Object.keys(payload).length === 1) throw new Error("No profile changes supplied");
      await api.patch("/user/profile", payload);
      toast.success("Profile updated"); setPwd(""); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const requestSecondEdit = async () => {
    setBusy(true);
    try {
      await api.post("/user/profile-change-request", { name: form.name, email: form.email, phone: form.phone, reason: reason || "Requesting approval for a second profile change." });
      toast.success("Profile change request submitted to Support"); setReason(""); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  if (loading || !data) return <PageTitle title="Profile" />;
  const counts = data.profile_edit_counts || {};
  const anyLocked = data.name_editable === false || data.email_editable === false || data.phone_editable === false;

  return <div className="space-y-6">
    <PageTitle title="Profile" subtitle="Manage your account details" />
    <div className="grid gap-6 lg:grid-cols-2">
      <GlassCard className="p-6">
        <div className="mb-4 flex items-center gap-2"><UserRound size={18} className="text-[var(--gold)]"/><h3 className="font-semibold text-white">Account Information</h3></div>
        <div className="space-y-4">
          <Field label="User / Member ID"><div className={ro}>{data.member_id}</div></Field>
          <Field label="Name"><div className={ro}>{data.name}</div></Field>
          <Field label="Registered Email"><div className={ro}>{data.email}</div></Field>
          <Field label="Contact No."><div className={ro}>{data.phone || "—"}</div></Field>
          <Field label="Rank"><div className={ro}>{data.rank}</div></Field>
        </div>
      </GlassCard>
      <GlassCard className="p-6">
        <h3 className="mb-4 font-semibold text-white">Edit Profile</h3>
        <form onSubmit={submit} className="space-y-3">
          <Field label={"Name " + (data.name_editable ? "(1 self-edit available)" : "(Support/Admin required)")}>
            <input className={inp} value={form.name || data.name || ""} onChange={e=>setForm({...form,name:e.target.value})} disabled={!data.name_editable}/>
          </Field>
          <Field label={"Email " + (data.email_editable ? "(1 self-edit available)" : "(Support/Admin required)")}>
            <input className={inp} type="email" value={form.email || data.email || ""} onChange={e=>setForm({...form,email:e.target.value})} disabled={!data.email_editable}/>
          </Field>
          <Field label={"Contact " + (data.phone_editable ? "(1 self-edit available)" : "(Support/Admin required)")}>
            <input className={inp} value={form.phone || data.phone || ""} onChange={e=>setForm({...form,phone:e.target.value})} disabled={!data.phone_editable}/>
          </Field>
          <Field label="Current Password"><input className={inp} type="password" value={pwd} onChange={e=>setPwd(e.target.value)} required/></Field>
          <LuxuryButton type="submit" disabled={busy || ![data.name_editable,data.email_editable,data.phone_editable].some(Boolean)}>Save Available Changes</LuxuryButton>
        </form>
        {anyLocked && <div className="mt-5 rounded-xl glass-2 p-4 text-xs text-[var(--text-dim)]">
          <p className="mb-2 font-semibold text-white">Need another change?</p>
          <p className="mb-3">Each field can be self-edited once. A second change must be requested through Support/Admin.</p>
          <textarea className={inp} rows={3} placeholder="Reason for the requested second change" value={reason} onChange={e=>setReason(e.target.value)}/>
          <LuxuryButton className="mt-2" type="button" onClick={requestSecondEdit} disabled={busy}>Request Support Approval</LuxuryButton>
        </div>}
        <div className="mt-5 flex items-start gap-2 rounded-xl glass-2 p-3 text-xs text-[var(--text-dim)]"><Lock size={14} className="mt-0.5 text-[var(--gold)]"/>
          Profile changes are password-protected, individually limited to one self-edit, and recorded in the audit log.
        </div>
        <div className="mt-3 text-xs text-[var(--text-dim)]">Self edits used — Name: {counts.name||0}, Email: {counts.email||0}, Contact: {counts.phone||0}</div>
      </GlassCard>
    </div>
  </div>;
}
function Field({ label, children }) { return <div><p className="mb-1.5 text-xs uppercase tracking-wider text-[var(--text-dim)]">{label}</p>{children}</div>; }
